import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import CommerceItemImage from "../../../../components/store/CommerceItemImage.jsx";
import PaymentPanel from "./PaymentPanel.jsx";
import { StatusBadge } from "../../../../components/ui/index.js";

const fmt = (n, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(n || 0));

const CLASSIFICATION_LABEL = { digital_only: "Solo digital", physical_only: "Solo físico", mixed: "Mixto" };

// El fulfillment es independiente por línea (MVP-02 §5): un mixto con
// digital disponible y físico pendiente sigue abierto, nunca se resume en un
// único estado de pedido.
const FULFILLMENT_TONE = {
    pendiente_pago: "neutral",
    pendiente_preparacion: "warning",
    pendiente_produccion: "warning",
    enviado: "info",
    entregado: "success",
    pendiente_habilitacion: "warning",
    disponible: "success",
    descargado: "success",
    cancelado: "error",
    incidencia: "error",
};

const UserOrderDetail = () => {
    const { orderNumber } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`/orders/mine/${orderNumber}/`, { withCredentials: true })
            .then(res => { if (!cancelled) setOrder(res.data); })
            .catch(err => {
                if (cancelled) return;
                setError(err.response?.status === 404
                    ? "Pedido no encontrado."
                    : (err.response?.data?.detail || "No se pudo cargar el pedido."));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [orderNumber]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="p-6">
                <Link to="/profile/mis-pedidos" className="link link-primary text-sm flex items-center gap-1 mb-4">
                    <ArrowLeft className="w-4 h-4" /> Volver
                </Link>
                <div role="alert" className="alert alert-error max-w-lg shadow-lg">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 flex flex-col gap-5">
            <Link to="/profile/mis-pedidos" className="link link-primary text-sm flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Mis pedidos
            </Link>

            <header className="glassCard rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold font-mono">{order.order_number}</h1>
                    <p className="text-xs text-base-content/60">
                        Creado el {new Date(order.created_at).toLocaleString("es-ES")}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="badge badge-ghost">{CLASSIFICATION_LABEL[order.classification] || "Físico"}</span>
                    <span className="badge badge-lg">{order.status_display || order.status}</span>
                </div>
            </header>

            <PaymentPanel orderNumber={order.order_number} />

            <section className="glassCard rounded-2xl p-5">
                <h2 className="font-semibold mb-3">Artículos</h2>
                <ul className="flex flex-col gap-3">
                    {(order.items || []).map(item => (
                        <li key={item.id} className="flex items-center justify-between gap-4 border-b border-white/10 pb-3 last:border-0">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <CommerceItemImage
                                    item={item}
                                    customKeyring={item.item_type === "digital" ? item.digital_package_detail : undefined}
                                    alt={item.snapshot_name}
                                    className="w-16 h-16"
                                />
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-medium truncate">{item.snapshot_name}</p>
                                        <StatusBadge
                                            label={item.fulfillment_status_display || item.fulfillment_status}
                                            tone={FULFILLMENT_TONE[item.fulfillment_status] || "neutral"}
                                            size="xs"
                                        />
                                    </div>
                                    {item.item_type === "custom_order" && (
                                        <p className="text-xs text-base-content/60">
                                            {item.snapshot_material} · {item.snapshot_color}
                                            {item.snapshot_finish ? ` · ${item.snapshot_finish}` : ""}
                                            {` · lote de ${item.quantity} uds.`}
                                        </p>
                                    )}
                                    {item.item_type === "product" && (
                                        <p className="text-xs text-base-content/60">
                                            {item.quantity} × {fmt(item.snapshot_unit_price, item.snapshot_currency)}
                                        </p>
                                    )}
                                    {item.item_type === "digital" && (
                                        <p className="text-xs text-base-content/60">
                                            Licencia {item.snapshot_required_license_version || "—"} · se habilita al confirmar el pago
                                        </p>
                                    )}
                                </div>
                            </div>
                            <p className="font-semibold whitespace-nowrap">
                                {fmt(item.snapshot_line_total, item.snapshot_currency)}
                            </p>
                        </li>
                    ))}
                </ul>
                <div className="border-t border-white/10 mt-3 pt-3 flex justify-between font-bold">
                    <span>Total</span>
                    <span>{fmt(order.total_price, order.currency)}</span>
                </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {order.classification === "digital_only" ? (
                    <div className="glassCard rounded-2xl p-5">
                        <h2 className="font-semibold mb-2">Entrega</h2>
                        <p className="text-sm text-base-content/70">
                            Pedido solo digital: sin dirección de envío ni coste de transporte.
                        </p>
                    </div>
                ) : (
                    <div className="glassCard rounded-2xl p-5">
                        <h2 className="font-semibold mb-2">Envío</h2>
                        <p className="text-sm">{order.shipping_name}</p>
                        <p className="text-sm">{order.shipping_address_line1}</p>
                        {order.shipping_address_line2 && <p className="text-sm">{order.shipping_address_line2}</p>}
                        <p className="text-sm">{order.shipping_postal_code} {order.shipping_city}</p>
                        {order.shipping_state && <p className="text-sm">{order.shipping_state}</p>}
                        <p className="text-sm">{order.shipping_country}</p>
                        {order.shipping_summary?.tracking_code && (
                            <div className="mt-3 pt-3 border-t border-white/10">
                                <p className="text-xs text-base-content/55">Localizador de {order.shipping_summary.carrier || "transporte"}</p>
                                <p className="font-mono text-sm font-semibold">{order.shipping_summary.tracking_code}</p>
                            </div>
                        )}
                        {(order.shipping_summary?.delivery_min_date || order.shipping_summary?.delivery_max_date) && (
                            <div className="mt-2">
                                <p className="text-xs text-base-content/55">Entrega estimada (no garantizada)</p>
                                <p className="text-sm">
                                    {order.shipping_summary.delivery_min_date} — {order.shipping_summary.delivery_max_date}
                                </p>
                            </div>
                        )}
                    </div>
                )}
                <div className="glassCard rounded-2xl p-5">
                    <h2 className="font-semibold mb-2">Contacto</h2>
                    <p className="text-sm">{order.contact_email}</p>
                    {order.contact_phone && <p className="text-sm">{order.contact_phone}</p>}
                    {order.notes && (
                        <>
                            <h3 className="font-semibold mt-3 mb-1 text-sm">Notas</h3>
                            <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default UserOrderDetail;
